import { useSceneStore } from '@/features/shared/stores/scene.store'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { cn } from '@/shared/lib/utils'
import { getSceneHeader } from '@/shared/utils/editor-utils'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { generateShotListAI } from '../service/ai-service'
import { Shot, useShotListStore } from '../store/useShotListStore'
import { EditableShotCard } from './EditableShotCard'
import ScenePreviewPanel from './ScenePreviewPanel'
import ShotListEmptyState from './ShotListEmptyState'
import { ShotListGenerationDialog, ShotListGenerationOptions } from './ShotListGenerationDialog'
import ShotListHeader from './ShotListHeader'
import ShotListNoSceneState from './ShotListNoSceneState'
import AddShotDialog from './AddShotDialog'
import { SerializedContent } from '@storyverse/shared'
import { getSceneTextContent } from '../utils/textUtils'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { useCharacters } from '@/features/characters/queries/character.queries'
import { inferCharacterVariantsFromScene } from '../utils/inferCharacterVariantsFromScene'

const ShotListMainContent = () => {
    const { project_id } = useParams<{ project_id: string }>()
    const {
        selectedSceneNode,
        shotLists,
        setShotList,
        setIsGenerating,
        isGenerating,
        addShot,
        showScenePreview,
        setShowScenePreview,
        loadShotList,
    } = useShotListStore()
    const { editor } = useSceneStore()
    const { project } = useWorkspace()
    const { data: characters = [] } = useCharacters(project?.id ?? '')
    const [sceneName, setSceneName] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
    const [isAIDialogOpen, setIsAIDialogOpen] = useState(false)

    // Load shot list from DB when scene is selected
    useEffect(() => {
        if (selectedSceneNode && project_id) {
            loadShotList(selectedSceneNode.id)
        }
    }, [selectedSceneNode, project_id, loadShotList])

    // Extract scene content and header when selectedSceneId changes
    useEffect(() => {
        if (selectedSceneNode) {
            const header = editor?.read(() => getSceneHeader(selectedSceneNode))
            setSceneName(header || 'Untitled Scene')
        }
    }, [selectedSceneNode, editor])

    const handleGenerateAI = async (options?: ShotListGenerationOptions) => {
        if (!selectedSceneNode) return

        const sceneContent = editor?.read(() => selectedSceneNode.getCurrentSceneContent()) as
            | SerializedContent[]
            | undefined
        console.log(selectedSceneNode)
        console.log('Scene content retrieved for AI generation:', sceneContent)
        const textToUse = sceneContent ? getSceneTextContent(sceneContent) : ''
        console.log('Generating with text length:', textToUse)

        if (
            !sceneContent ||
            sceneContent.length === 0 ||
            !textToUse ||
            textToUse.trim().length === 0
        ) {
            console.error('No scene text found')
            setError('No text content found for this scene. Please add content before generating.')
            return
        }

        setIsGenerating(true)
        setError(null)
        console.log('Starting AI generation for scene:', selectedSceneNode)

        try {
            const inferredCharacterVariants = inferCharacterVariantsFromScene(
                sceneContent,
                characters
            )
            const shots = await generateShotListAI({
                ...(options || { mode: 'auto' }),
                sceneContent,
                characterVariants:
                    options?.mode === 'custom'
                        ? options.characterVariants
                        : inferredCharacterVariants,
            })
            setShotList(selectedSceneNode.id, shots)
            setIsAIDialogOpen(false)
        } catch (error: any) {
            console.error('AI Generation failed', error)
            setError(
                error.message ||
                    'Failed to generate shot list. Please check your connection and try again.'
            )
            setIsAIDialogOpen(false)
        } finally {
            setIsGenerating(false)
        }
    }

    const handleAddShot = (shotData?: Omit<Shot, 'id'>) => {
        if (!selectedSceneNode) return

        const newShot: Shot = {
            id: crypto.randomUUID(),
            description: shotData?.description ?? '',
            shotType: shotData?.shotType ?? 'Other',
            cameraMovement: shotData?.cameraMovement ?? 'Static',
            cameraAngle: shotData?.cameraAngle ?? 'Eye Level',
            framing: shotData?.framing ?? 'Other',
            subject: shotData?.subject ?? '',
            emotionalPurpose: shotData?.emotionalPurpose ?? '',
            duration: shotData?.duration ?? '',
            shotPriority: shotData?.shotPriority ?? 'Good to Have',
            characterVariants: shotData?.characterVariants ?? [],
        }

        addShot(selectedSceneNode.id, newShot)
    }

    if (!selectedSceneNode) {
        return <ShotListNoSceneState />
    }

    const currentShotList = shotLists[selectedSceneNode.id]

    return (
        <div className="flex flex-1 w-full h-full overflow-hidden bg-background min-w-0">
            <div className="flex w-full h-full overflow-hidden min-w-0">
                {/* Shot List Panel */}
                <div
                    className={cn(
                        'flex flex-col h-full overflow-hidden transition-all duration-300 min-w-0',
                        showScenePreview ? 'w-2/3 lg:w-3/4' : 'w-full'
                    )}
                >
                    <ShotListHeader
                        sceneName={sceneName}
                        shotCount={currentShotList?.length || 0}
                        showScenePreview={showScenePreview}
                        setShowScenePreview={setShowScenePreview}
                        setIsAIDialogOpen={setIsAIDialogOpen}
                        error={error}
                        hasShots={currentShotList?.length > 0}
                        onAddShot={() => setIsAddDialogOpen(true)}
                    />

                    <div className="flex-1 overflow-hidden h-full">
                        <ScrollArea className="h-full w-full">
                            <div className="p-6 max-w-5xl mx-auto">
                                {!currentShotList || currentShotList.length === 0 ? (
                                    <ShotListEmptyState
                                        sceneName={sceneName}
                                        error={error}
                                        setIsAIDialogOpen={setIsAIDialogOpen}
                                        handleCreateManually={() => setIsAddDialogOpen(true)}
                                    />
                                ) : (
                                    <div className="flex flex-col gap-6 pb-20">
                                        {currentShotList.map((shot, idx) => (
                                            <EditableShotCard
                                                key={shot.id || idx}
                                                shot={shot}
                                                sceneId={selectedSceneNode.id}
                                                index={idx}
                                                totalShots={currentShotList.length}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </div>
                <ShotListGenerationDialog
                    open={isAIDialogOpen}
                    onOpenChange={setIsAIDialogOpen}
                    onGenerate={handleGenerateAI}
                    isLoading={isGenerating}
                />
                <AddShotDialog
                    open={isAddDialogOpen}
                    onOpenChange={setIsAddDialogOpen}
                    onAddShot={handleAddShot}
                />

                {/* Preview Panel */}
                {showScenePreview && (
                    <div className="w-1/3 lg:w-1/4 border-l flex flex-col h-full bg-background transition-all duration-300">
                        <ScenePreviewPanel className="w-full h-full" />
                    </div>
                )}
            </div>
        </div>
    )
}

export default ShotListMainContent
